import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, X, RotateCcw, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { useNespresso, NESPRESSO_TIERS } from '../context/NespressoContext';
import { useSearchParams, useLocation } from 'react-router-dom';
import { TIERS, getTierByPodCount, getNextTier } from '../config/tiers';

// Near-milestone presets (5 below each threshold)
const PRESETS = [
  { label: 'Near Espresso Explorer (95)', value: 95, tierId: 'espresso-explorer' },
  { label: 'Near Lungo Legend (495)', value: 495, tierId: 'lungo-legend' },
  { label: 'Near Barista Bronze (995)', value: 995, tierId: 'barista-bronze' },
  { label: 'Near Crema Connoisseur (2495)', value: 2495, tierId: 'crema-connoisseur' },
  { label: 'Near Master Roaster (4995)', value: 4995, tierId: 'master-roaster' },
  { label: 'Near Grand Cru Guardian (9995)', value: 9995, tierId: 'grand-cru-guardian' },
];

/**
 * DemoControls - Developer tool for testing gamification features
 *
 * NOTE: This component is only functional on Nespresso routes where
 * the NespressoContext provides gamification state. On other routes,
 * it shows a simplified interface with local state.
 */
const NespressoDemoControls: React.FC = () => {
  const { state, dispatch, currentTier, nextTier, podsToNext } = useNespresso();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [cornerClicks, setCornerClicks] = useState(0);
  const [showPresets, setShowPresets] = useState(false);
  const [memberEmail, setMemberEmail] = useState(state.userEmail || '');
  const [demoMode, setDemoMode] = useState(false);
  const [lifetimePods, setLifetimePods] = useState(state.gamification.lifetimePods);

  // Check for ?demo=true URL parameter
  const demoParam = searchParams.get('demo') === 'true';

  // Only show full controls on Nespresso routes
  const isNespressoRoute = location.pathname.startsWith('/nespresso');

  // Track corner clicks for hidden toggle (5 clicks to toggle)
  const handleCornerClick = useCallback(() => {
    setCornerClicks(prev => {
      const newCount = prev + 1;
      if (newCount >= 5) {
        setDemoMode(prev => !prev);
        return 0;
      }
      return newCount;
    });

    // Reset counter after 2 seconds of no clicks
    setTimeout(() => setCornerClicks(0), 2000);
  }, []);

  // Keyboard shortcut: Ctrl+Shift+D
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setDemoMode(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Enable demo mode if URL param is present
  React.useEffect(() => {
    if (demoParam && !demoMode) {
      setDemoMode(true);
    }
  }, [demoParam, demoMode]);

  // Sync local state with context
  React.useEffect(() => {
    setLifetimePods(state.gamification.lifetimePods);
  }, [state.gamification.lifetimePods]);

  // Don't render anything if demo mode is not enabled and no URL param
  const shouldShowControls = demoMode || demoParam;

  // Handle pod count change
  const handlePodCountChange = (value: number) => {
    const newValue = Math.max(0, Math.min(12000, value));
    setLifetimePods(newValue);
    dispatch({ type: 'SET_LIFETIME_PODS', payload: newValue });
  };

  // Handle reset
  const handleReset = () => {
    setLifetimePods(0);
    setMemberEmail('');
    dispatch({ type: 'RESET_GAMIFICATION' });
  };

  // Handle email change
  const handleEmailChange = (email: string) => {
    setMemberEmail(email);
    if (email) {
      dispatch({ type: 'SET_EMAIL', payload: email });
    }
  };

  // Add pods (simulate recycling)
  const handleAddPods = (amount: number) => {
    const newValue = Math.min(12000, lifetimePods + amount);
    setLifetimePods(newValue);
    dispatch({ type: 'SET_LIFETIME_PODS', payload: newValue });
  };

  // Trigger celebration for a specific tier
  const handleTriggerCelebration = (tierId: string) => {
    dispatch({ type: 'TRIGGER_MILESTONE', payload: tierId });
  };

  return (
    <>
      {/* Hidden corner click area - always present */}
      <div
        className="fixed bottom-0 right-0 w-12 h-12 z-40 cursor-pointer"
        onClick={handleCornerClick}
        title={`Click ${5 - cornerClicks} more times to toggle demo mode`}
      />

      {/* Demo toggle button - subtle when controls not shown */}
      {!shouldShowControls && (
        <button
          className="fixed bottom-4 right-4 opacity-10 hover:opacity-30 transition-opacity duration-200 text-xs text-gray-500 z-40"
          onClick={() => setDemoMode(true)}
        >
          Demo
        </button>
      )}

      {/* Demo controls panel */}
      <AnimatePresence>
        {shouldShowControls && (
          <>
            {/* Toggle button when controls are enabled but panel is closed */}
            {!isOpen && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="fixed bottom-4 right-4 bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-full shadow-lg z-50 transition-colors"
                onClick={() => setIsOpen(true)}
                title="Open Demo Controls"
              >
                <Settings size={24} />
              </motion.button>
            )}

            {/* Main panel */}
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="fixed bottom-4 right-4 w-80 bg-white rounded-lg shadow-2xl z-50 border border-gray-200 overflow-hidden"
              >
                {/* Header */}
                <div className="bg-indigo-600 text-white px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Settings size={18} />
                    <span className="font-semibold text-sm">Nespresso Demo Controls</span>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="hover:bg-indigo-700 p-1 rounded transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                  {/* Nespresso Route Info */}
                  {!isNespressoRoute && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                      <p className="font-medium mb-1">Limited Mode</p>
                      <p>Navigate to <code className="bg-amber-100 px-1 rounded">/nespresso</code> for full gamification controls.</p>
                    </div>
                  )}

                  {/* Current State Display */}
                  <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Lifetime Pods:</span>
                      <span className="font-mono font-semibold">{lifetimePods.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Current Tier:</span>
                      <span className="font-semibold text-indigo-600">{currentTier.name}</span>
                    </div>
                    {nextTier && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Next Tier:</span>
                        <span className="text-gray-600">{nextTier.name} ({podsToNext} to go)</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Route:</span>
                      <span className="font-mono text-gray-600 truncate max-w-[140px]">{location.pathname}</span>
                    </div>
                  </div>

                  {/* Pod Count Slider */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Set Lifetime Pods
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min={0}
                        max={12000}
                        step={1}
                        value={lifetimePods}
                        onChange={(e) => handlePodCountChange(parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                      <input
                        type="number"
                        min={0}
                        max={12000}
                        value={lifetimePods}
                        onChange={(e) => handlePodCountChange(parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1 text-xs border rounded text-center font-mono"
                      />
                    </div>
                  </div>

                  {/* Quick Add Buttons */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Quick Add Pods
                    </label>
                    <div className="flex gap-2">
                      {[1, 5, 10, 50, 100].map(amount => (
                        <button
                          key={amount}
                          onClick={() => handleAddPods(amount)}
                          className="flex-1 px-2 py-1.5 text-xs bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors font-medium"
                        >
                          +{amount}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Presets Section */}
                  <div>
                    <button
                      onClick={() => setShowPresets(!showPresets)}
                      className="flex items-center justify-between w-full text-xs font-medium text-gray-700 mb-2 hover:text-indigo-600 transition-colors"
                    >
                      <span>Near-Milestone Presets</span>
                      {showPresets ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    <AnimatePresence>
                      {showPresets && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="space-y-1 overflow-hidden"
                        >
                          {PRESETS.map(preset => (
                            <button
                              key={preset.value}
                              onClick={() => handlePodCountChange(preset.value)}
                              className="w-full text-left px-3 py-2 text-xs bg-amber-50 hover:bg-amber-100 text-amber-800 rounded transition-colors"
                            >
                              {preset.label}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Trigger Celebration */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Trigger Celebration
                    </label>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleTriggerCelebration(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="w-full px-3 py-2 text-xs border rounded bg-white cursor-pointer hover:border-indigo-400 transition-colors"
                      defaultValue=""
                    >
                      <option value="">Select a tier to celebrate...</option>
                      {NESPRESSO_TIERS.filter(t => t.id !== 'first-sip').map(tier => (
                        <option key={tier.id} value={tier.id}>
                          {tier.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Set Member Email */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Member Email
                    </label>
                    <input
                      type="email"
                      value={memberEmail}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      placeholder="demo@example.com"
                      className="w-full px-3 py-2 text-xs border rounded focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-colors"
                    />
                    <div className="flex gap-2 mt-2">
                      {['vip@test.com', 'new@test.com', 'loyal@test.com'].map(email => (
                        <button
                          key={email}
                          onClick={() => handleEmailChange(email)}
                          className="flex-1 px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors truncate"
                        >
                          {email.split('@')[0]}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Celebrating indicator */}
                  {state.gamification.celebratingTierId && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-purple-700 text-xs">
                        <Sparkles size={14} />
                        <span>
                          Celebrating: <strong>{NESPRESSO_TIERS.find(t => t.id === state.gamification.celebratingTierId)?.name}</strong>
                        </span>
                      </div>
                      <button
                        onClick={() => dispatch({ type: 'DISMISS_CELEBRATION' })}
                        className="mt-2 w-full text-xs text-purple-600 hover:text-purple-800 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {/* Reset Button */}
                  <button
                    onClick={handleReset}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-xs font-medium"
                  >
                    <RotateCcw size={14} />
                    Reset All Progress
                  </button>

                  {/* Disable Demo Mode */}
                  <button
                    onClick={() => setDemoMode(false)}
                    className="w-full text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Disable Demo Mode
                  </button>
                </div>
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NespressoDemoControls;
